import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ChevronRight, ChevronDown, Lock, Search, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { InboxBadge } from "@/components/app/AppShell";
import { visibleSections, allLeaves } from "@/components/app/navConfig";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const BOTTOM_IDS = ["today", "shift-ops", "close", "inventory"];
const row = "flex items-center gap-3 px-4 min-h-[52px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900/30";

function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="flex items-center gap-2 h-10 pl-1 pr-3 rounded-full border border-[#E6E1D7] bg-white max-w-[180px]" aria-label="Account" data-testid="more-account-btn">
          <span className="h-8 w-8 rounded-full bg-[#9E2A2B] text-white flex items-center justify-center text-sm font-semibold shrink-0">{user?.name?.[0]}</span>
          <span className="text-sm font-medium truncate">{user?.name?.split(" ")[0]}</span><ChevronDown className="h-3.5 w-3.5 text-stone-500 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0 bg-white" data-testid="more-account-panel">
        <div className="px-4 py-3 border-b"><div className="font-medium truncate">{user?.name}</div><div className="text-xs text-stone-500 truncate">{user?.email}</div></div>
        <Link to="/app/notifications" className={`${row} text-sm`} data-testid="more-account-notifications"><Bell className="h-4 w-4" />Notification preferences</Link>
        <button type="button" onClick={async () => { await logout(); nav("/login"); }} className={`${row} w-full text-sm text-[#9E2A2B] font-medium border-t`} data-testid="more-logout"><LogOut className="h-4 w-4" />Log out</button>
      </PopoverContent>
    </Popover>
  );
}

function Group({ s, open, onToggle }) {
  if (s.children.length === 1) return <Link to={s.to} className={row} data-testid={`more-group-${s.id}`}><s.icon className="h-5 w-5 text-stone-600" /><span className="flex-1 font-medium">{s.label}</span><ChevronRight className="h-4 w-4 text-stone-400" /></Link>;
  return (
    <div>
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={`more-children-${s.id}`} className={`${row} w-full text-left`} data-testid={`more-group-${s.id}`}>
        <s.icon className="h-5 w-5 text-stone-600" /><span className="flex-1 font-medium">{s.label}</span>
        {!open && s.children.some((c) => c.badge) && <InboxBadge className="mr-2" />}
        <ChevronDown className={`h-4 w-4 text-stone-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div id={`more-children-${s.id}`} className="pb-2 bg-[#FBF9F5]" data-testid={`more-children-${s.id}`}>{s.children.map((c) => (
        <Link key={c.id} to={c.to} className={`${row} pl-12 min-h-[44px] text-sm`} data-testid={`more-child-${c.id}`}>
          <span className="flex-1">{c.label}</span>{c.badge && <InboxBadge className="mr-2" />}{c.locked && <Lock className="h-3.5 w-3.5 text-stone-400" aria-label="Requires a plan upgrade" />}<ChevronRight className="h-4 w-4 text-stone-300" />
        </Link>))}</div>}
    </div>
  );
}

export default function More() {
  const sections = visibleSections(useAuth());
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const groups = sections.filter((s) => !BOTTOM_IDS.includes(s.id));
  const hits = q.trim() ? allLeaves(sections).filter((l) => l.search.includes(q.trim().toLowerCase())) : null;
  return (
    <div data-testid="more-page" className="max-w-xl">
      <div className="flex items-center justify-between gap-3 mb-4"><h1 className="font-display text-3xl">More</h1><Account /></div>
      <label className="flex items-center gap-2 h-12 px-4 mb-4 rounded-xl border bg-white"><Search className="h-4 w-4 text-stone-400" />
        <input className="flex-1 outline-none text-base bg-transparent" placeholder="Find a page (e.g. schedule, tips, invoices)" aria-label="Find a page" value={q} onChange={(e) => setQ(e.target.value)} data-testid="more-search" /></label>
      {hits ? (
        <div className="card-surface divide-y" data-testid="more-results">{hits.map((l) => (
          <Link key={l.id} to={l.to} className={row} data-testid={`more-result-${l.id}`}><span className="flex-1"><span className="block font-medium text-sm">{l.label}</span><span className="block text-xs text-stone-500">{l.path}</span></span><ChevronRight className="h-4 w-4 text-stone-400" /></Link>))}
          {!hits.length && <div className="p-4 text-sm text-stone-500" data-testid="more-no-match">No page matches “{q}”.</div>}</div>
      ) : (
        <div className="card-surface divide-y" data-testid="more-groups">{groups.map((s) => <Group key={s.id} s={s} open={open === s.id} onToggle={() => setOpen((o) => (o === s.id ? null : s.id))} />)}</div>
      )}
    </div>
  );
}
